import React from "react";
import { Monitor, Smartphone, X } from "lucide-react";

/**
 * What the customer will actually see.
 *
 * A popup is a piece of creative, and the only honest way to check one is to
 * look at it — nobody should have to publish a campaign to find out that the
 * Arabic headline overflows the button.
 *
 * Two things this deliberately reproduces rather than approximates:
 *
 *   - The locale fallback. The storefront picks `ar_*` on Arabic and falls
 *     back to the base column when it is empty, so a campaign with one shared
 *     image fills only `image`. Previewing Arabic with the English artwork is
 *     the correct answer, not a gap to fill in.
 *
 *   - The mobile shape. On a phone this is a bottom sheet covering part of the
 *     screen, not a full-screen wall — Google treats an interstitial that
 *     hides content on arrival from search as an intrusive pattern, and the
 *     storefront renders it accordingly. A preview that showed a centred card
 *     on both would hide the layout that actually ships.
 */

const pick = (base, arabic, isArabic) => {
  if (!isArabic) return base || "";
  return arabic || base || "";
};

const PromoPopupPreview = ({ form, coupon, language, device, onLanguageChange, onDeviceChange }) => {
  const isArabic = language === "ar";

  const image = pick(form.image, form.ar_image, isArabic);
  const headline = pick(form.headline, form.ar_headline, isArabic);
  const body = pick(form.body, form.ar_body, isArabic);
  const ctaLabel = pick(form.cta_label, form.ar_cta_label, isArabic);

  const hasContent = Boolean(image || headline || body || ctaLabel);

  return (
    <div className="pp-preview">
      <div className="pp-preview__bar">
        <span className="pp-preview__title">Preview</span>
        <div className="pp-preview__toggles">
          <div className="pp-seg" role="group" aria-label="Preview language">
            {[
              { value: "en", label: "EN" },
              { value: "ar", label: "AR" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`pp-seg__btn${language === opt.value ? " pp-seg__btn--on" : ""}`}
                onClick={() => onLanguageChange(opt.value)}
                aria-pressed={language === opt.value}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="pp-seg" role="group" aria-label="Preview device">
            {[
              { value: "desktop", label: "Desktop", Icon: Monitor },
              { value: "mobile", label: "Mobile", Icon: Smartphone },
            ].map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                className={`pp-seg__btn${device === value ? " pp-seg__btn--on" : ""}`}
                onClick={() => onDeviceChange(value)}
                aria-pressed={device === value}
                aria-label={label}
                title={label}
              >
                <Icon size={13} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={`pp-stage pp-stage--${device}`}>
        <div className="pp-stage__page" aria-hidden="true">
          <span className="pp-stage__line" style={{ width: "72%" }} />
          <span className="pp-stage__line" style={{ width: "54%" }} />
          <span className="pp-stage__line" style={{ width: "63%" }} />
          <span className="pp-stage__line" style={{ width: "40%" }} />
        </div>

        <div className="pp-stage__scrim" />

        <div
          className={`pp-card pp-card--${device}`}
          dir={isArabic ? "rtl" : "ltr"}
          lang={isArabic ? "ar" : "en"}
        >
          <button type="button" className="pp-card__close" aria-label="Close preview popup" tabIndex={-1}>
            <X size={14} />
          </button>

          {!hasContent ? (
            <p className="pp-card__placeholder">
              Add artwork or a headline and it will appear here.
            </p>
          ) : (
            <>
              {image ? (
                <img className="pp-card__image" src={image} alt="" />
              ) : null}
              <div className="pp-card__text">
                {headline ? <h3 className="pp-card__headline">{headline}</h3> : null}
                {body ? <p className="pp-card__body">{body}</p> : null}
                {coupon ? (
                  <div className="pp-card__code">
                    <span className="pp-card__code-value">{coupon.coupon_code}</span>
                    <span className="pp-card__code-copy">
                      {isArabic ? "نسخ" : "Copy"}
                    </span>
                  </div>
                ) : null}
                {ctaLabel ? (
                  <span className="pp-card__cta">{ctaLabel}</span>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      <p className="pp-preview__note">
        {device === "mobile"
          ? "On a phone it rises from the bottom and covers part of the screen — a full-screen block on arrival from Google can cost search ranking."
          : "On desktop it sits centred over a dimmed page."}
        {" "}
        Appears after {form.delay_seconds || 0}s.
      </p>
    </div>
  );
};

export default PromoPopupPreview;
